import React, { useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { SERVICES } from '@/data/site-config';

export function LeadForm({ source = '/', compact = false, variant = 'card', interests = SERVICES, testIdPrefix = 'lead' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: '', message: '', consent: true, company: '' });
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim()) { setErr('Please enter your name.'); return; }
    if (!form.email.trim() && !form.phone.trim()) { setErr('Enter at least an email or phone number.'); return; }
    if (!form.consent) { setErr('Please accept the consent checkbox.'); return; }
    try {
      setStatus('submitting');
      await api.submitLead({
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        interest: form.interest || undefined,
        message: form.message.trim() || undefined,
        consent: form.consent,
        source_page: source,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        utm: parseUtm(),
        company: form.company,  // honeypot
      });
      setStatus('success');
      setTimeout(() => navigate('/thank-you'), 300);
    } catch (e2) {
      setStatus('error');
      setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again or call us.');
    }
  };

  const wrap = variant === 'card' ? 'surface p-6 md:p-7 glow' : '';

  return (
    <form onSubmit={submit} className={wrap} aria-labelledby="lead-form-title" data-testid={`${testIdPrefix}-form`}>
      <h3 id="lead-form-title" className="font-display text-xl font-semibold mb-1">Request a free consultation</h3>
      <p className="text-sm text-[color:var(--ft-dim)] mb-5">Tell us a little about your goal. We’ll follow up within a business day.</p>

      <div className={`grid gap-3 ${compact ? '' : 'md:grid-cols-2'}`}>
        <div className={compact ? '' : 'md:col-span-2'}>
          <label className="label" htmlFor="lead-name">Name</label>
          <input id="lead-name" className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your full name" autoComplete="name" required data-testid={`${testIdPrefix}-name`} />
        </div>
        <div>
          <label className="label" htmlFor="lead-email">Email</label>
          <input id="lead-email" className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" autoComplete="email" data-testid={`${testIdPrefix}-email`} />
        </div>
        <div>
          <label className="label" htmlFor="lead-phone">Phone</label>
          <input id="lead-phone" className="input" type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(###) ###-####" autoComplete="tel" data-testid={`${testIdPrefix}-phone`} />
        </div>
        <div className={compact ? '' : 'md:col-span-2'}>
          <label className="label" htmlFor="lead-interest">Primary goal</label>
          <select id="lead-interest" className="select" value={form.interest} onChange={(e) => set('interest', e.target.value)} data-testid={`${testIdPrefix}-interest`}>
            <option value="">Choose what you’re interested in</option>
            {interests.map((s) => (<option key={s.slug} value={s.slug}>{s.title}</option>))}
            <option value="general">Not sure yet</option>
          </select>
        </div>
        <div className={compact ? '' : 'md:col-span-2'}>
          <label className="label" htmlFor="lead-message">Anything we should know? (optional)</label>
          <textarea id="lead-message" className="textarea" value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Goal, schedule, injuries, etc." data-testid={`${testIdPrefix}-message`} />
        </div>
      </div>

      {/* honeypot */}
      <div style={{ position: 'absolute', left: '-10000px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
        <label>Company<input value={form.company} onChange={(e) => set('company', e.target.value)} tabIndex="-1" /></label>
      </div>

      <label className="flex items-start gap-2 mt-4 text-sm text-[color:var(--ft-muted)]">
        <input type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} className="mt-1 h-4 w-4 accent-[color:var(--ft-accent)]" data-testid={`${testIdPrefix}-consent`} />
        <span>I agree to be contacted by Fitness Training 360 about my inquiry. Standard message/data rates may apply.</span>
      </label>

      {err && (<p className="error-text mt-3 inline-flex items-center gap-1"><AlertCircle size={14} /> {err}</p>)}

      <button type="submit" className="btn btn-primary btn-lg w-full mt-5" disabled={status === 'submitting'} data-testid={`${testIdPrefix}-submit`}>
        {status === 'submitting' ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : status === 'success' ? (<><CheckCircle2 size={18} /> Sent</>) : 'Get my free consultation'}
      </button>
      <p className="help mt-3 text-center">Prefer to talk? <a href="tel:+17724005743" className="text-[color:var(--ft-accent)] hover:underline">Call 772-400-5743</a></p>
    </form>
  );
}

function parseUtm() {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
      const v = params.get(k); if (v) utm[k] = v;
    });
    return utm;
  } catch (_) { return {}; }
}
